import { apolloProvider } from '@/apollo-provider.js'
import { fetchDataWithCache } from '@/helpers/cacheHelper'
import {
  QUERY_GET_A_HUB_ANALYTICS_CONFIGURATION,
  QUERY_GET_ALL_RFV_ANALYTICS_PERIODS,
  QUERY_GET_RFV_CLIENTS_METRICS,
  QUERY_GET_RFV_PERIOD_MIGRATIONS,
  QUERY_GET_A_HUB_ANALYTICS_CONFIGURATIONS,
} from '../graphql'

const MAX_POLLING_TRIES = 300

export default {
  async getAnalyticsConfigurations({ commit }, { variables }) {
    try {
      commit('SET_LOADING_DRE_ANALYTICS_CONFIGURATIONS', true)
      const {
        data: {
          aHubAnalyticsConfigurations: { aHubAnalyticsConfigurations },
        },
      } = await apolloProvider.defaultClient
        .query({
          query: QUERY_GET_A_HUB_ANALYTICS_CONFIGURATIONS,
          variables,
          fetchPolicy: 'no-cache',
        })
        .then((data) => {
          return data
        })
      if (variables.type === 'RFV') commit('SET_RFV_ANALYTICS_CONFIGURATIONS', aHubAnalyticsConfigurations)
      else commit('SET_DRE_ANALYTICS_CONFIGURATIONS', aHubAnalyticsConfigurations)
    } catch (error) {
      console.log(error)
    } finally {
      commit('SET_LOADING_DRE_ANALYTICS_CONFIGURATIONS', false)
    }
  },
  async getAnalyticsConfiguration({ rootState, state, commit }, params) {
    // Logic to avoid multiple requests from multiple components invoking
    // the same action. Instead of doubling the requests, the component returns
    // a promise with a poll to wait for the other request to finish.
    if (state.isFetchingAnalyticsConfiguration) {
      return new Promise((resolve, reject) => {
        let intervalId = null
        let maxPollingTries = 0
        const pollFunction = () => {
          maxPollingTries += 1
          if (!state.isFetchingAnalyticsConfiguration) {
            clearInterval(intervalId)
            resolve()
          } else if (maxPollingTries > MAX_POLLING_TRIES) {
            reject(new Error(`Maximum number of polling tries reached: ${MAX_POLLING_TRIES}`))
          }
        }
        intervalId = setInterval(pollFunction, 100)
      })
    }
    commit('SET_IS_FETCHING_ANALYTICS_CONFIGURATION', true)
    try {
      const { lastUpdate } = rootState

      const fetchFunction = async () => {
        const response = await apolloProvider.defaultClient
          .query({
            query: QUERY_GET_A_HUB_ANALYTICS_CONFIGURATION,
            variables: {
              type: params.type,
            },
            fetchPolicy: 'no-cache',
          })
          .then(({ data: aHubAnalyticsConfiguration }) => {
            return aHubAnalyticsConfiguration
          })
        return response.aHubAnalyticsConfiguration
      }

      const aHubAnalyticsConfiguration = params.useCache
        ? await fetchDataWithCache('aHubAnalyticsConfiguration', fetchFunction, lastUpdate?.last_updated, lastUpdate?.processing_uuid)
        : await fetchFunction()

      commit('SET_ANALYTICS_CONFIGURATION', aHubAnalyticsConfiguration)
      commit('SET_IS_FETCHING_ANALYTICS_CONFIGURATION', false)
    } catch (error) {
      commit('SET_IS_FETCHING_ANALYTICS_CONFIGURATION', false)
      throw error
    }
  },
  async getAllAnalyticPeriods({ rootState, state, commit }, params) {
    // Logic to avoid multiple requests from multiple components invoking
    // the same action. Instead of doubling the requests, the component returns
    // a promise with a poll to wait for the other request to finish.
    if (state.isFetchingAllAnalyticPeriods) {
      return new Promise((resolve, reject) => {
        let intervalId = null
        let maxPollingTries = 0
        const pollFunction = () => {
          maxPollingTries += 1
          if (!state.isFetchingAllAnalyticPeriods) {
            clearInterval(intervalId)
            resolve()
          } else if (maxPollingTries > MAX_POLLING_TRIES) {
            reject(new Error(`Maximum number of polling tries reached: ${MAX_POLLING_TRIES}`))
          }
        }
        intervalId = setInterval(pollFunction, 100)
      })
    }
    commit('SET_IS_FETCHING_ALL_ANALYTIC_PERIODS', true)
    try {
      const { lastUpdate } = rootState

      const fetchFunction = async () => {
        const response = await apolloProvider.defaultClient
          .query({
            variables: {
              analyticConfigurationsIds: params.analyticConfigurationsIds,
            },
            query: QUERY_GET_ALL_RFV_ANALYTICS_PERIODS,
            fetchPolicy: 'no-cache',
          })
          .then((data) => {
            return data
          })
        return response?.data?.aHubAnalyticsPeriods
      }

      const aHubAnalyticsPeriods = params.useCache
        ? await fetchDataWithCache('aHubAnalyticsPeriods', fetchFunction, lastUpdate?.last_updated, lastUpdate?.processing_uuid)
        : await fetchFunction()

      if (aHubAnalyticsPeriods?.aHubAnalyticsPeriods?.length > 0) {
        commit('SET_ALL_ANALYTIC_PERIODS', aHubAnalyticsPeriods.aHubAnalyticsPeriods)
      }
      commit('SET_IS_FETCHING_ALL_ANALYTIC_PERIODS', false)
    } catch (error) {
      commit('SET_IS_FETCHING_ALL_ANALYTIC_PERIODS', false)
      throw error
    }
  },
  setCurrentPeriod({ commit }, period) {
    commit('SET_CURRENT_PERIOD', period)
  },
  setReferencePeriod({ commit }, period) {
    commit('SET_REFERENCE_PERIOD', period)
  },
  async getClientsMetrics({ commit }, { variables, periodType }) {
    const {
      data: { rfvClientsMetrics },
    } = await apolloProvider.defaultClient
      .query({
        query: QUERY_GET_RFV_CLIENTS_METRICS,
        variables,
        fetchPolicy: 'no-cache',
      })
      .then((data) => {
        return data
      })
    commit('SET_CLIENTS_METRICS', { key: periodType, payload: rfvClientsMetrics })
  },
  async getPeriodMigrations({ commit }, variables) {
    const { rfvPeriodMigrations } = await apolloProvider.defaultClient
      .query({
        query: QUERY_GET_RFV_PERIOD_MIGRATIONS,
        variables,
        fetchPolicy: 'no-cache',
      })
      .then(({ data: rfvPeriodMigrations }) => {
        return rfvPeriodMigrations
      })
    commit('SET_PERIOD_MIGRATIONS', rfvPeriodMigrations)
  },
}
